/**
 * @lines98/client/services/leaderboard-api — typed wrapper вокруг
 * `GET /daily/leaderboard` (T-8 server endpoint).
 *
 * Per core-daily-board.md §6 + anti-cheat.md §3A.4: the daily leaderboard is a
 * PUBLIC read-endpoint that works for guests AND authenticated viewers. Bearer
 * (api-client.ts, ADR-0006 §D2) is attached automatically when an identity
 * exists — that's what lets the server reveal the viewer's OWN `short_id` on the
 * live board (§6.3). A guest gets the same board with every live foreign
 * `short_id` withheld (`null`).
 *
 * **Foundational invariant Anti-shadow-practice (server-enforced, NOT here).**
 * This wrapper renders behaviour purely off the SERVER shape it receives —
 * specifically the presence/absence of `short_id` per row (anti-cheat.md §3A.4:
 * the live/past visibility split is decided server-side in
 * `leaderboard/daily-leaderboard.ts`; a client guard «if today, hide replay»
 * would be DevTools-bypassable). This wrapper does NOT add any such guard — it
 * just camelCase-maps the wire shape and surfaces it.
 *
 * Player name + tag: the SERVER now sends `player_name` (the nick or «Игрок») and
 * `player_tag` (the `#`-discriminator suffix) SEPARATELY — single source-of-truth
 * (ADR-0006 rev3 §3 + spec §5.1 / §5.3 / Q-6; owner-decision 2026-06-09). The client
 * renders both VERBATIM (no client-side name building, can't be forged) and styles the
 * tag smaller + muted (subordinate `#tag`). The shared `playerDisplayParts` is
 * kept ONLY as a defensive fallback if a server row omits the fields (shape-guard /
 * older server), so a malformed row still renders a non-PII name + tag (no
 * NaN/undefined).
 *
 * Error handling mirrors daily-api.ts: a discriminated union
 * ({ ok: true, ... } | { ok: false, error }). Network / 5xx → retry-able;
 * 429 → rate_limited. UI surfaces a retry affordance.
 *
 * Cross-references:
 *   - packages/server/src/routes/daily-leaderboard.ts (contract source — JSON shape).
 *   - packages/server/src/leaderboard/daily-leaderboard.ts (row shape + §6.3 rules).
 *   - services/api-client.ts (Bearer injection + envelope shape).
 *   - @lines98/shared/util — playerDisplayParts (shared name+tag fallback derivation).
 *   - core-daily-board.md §6 / §6.3 / AC-14 / AC-15; anti-cheat.md §3A.4.
 */

import { playerDisplayParts } from '@lines98/shared/util';
import {
  ALL_TIME_LEADERBOARD_DEFAULT_LIMIT,
  ALL_TIME_LEADERBOARD_MAX_LIMIT,
  DAILY_LEADERBOARD_DEFAULT_LIMIT,
  DAILY_LEADERBOARD_MAX_LIMIT,
} from '@lines98/shared/config/balance';
import type { GameMode } from '@lines98/shared/types/game';

import { apiClient } from './api-client.js';

// =============================================================================
// Public types (camelCase — mapped from snake_case wire shape)
// =============================================================================

/**
 * One leaderboard row, camelCase-mapped from the server `DailyLeaderboardRow`.
 *
 * `shortId` carries the §6.3 visibility decision the SERVER already made:
 *   - present (≠ null) → the replay is openable → row is PLAYABLE.
 *   - null → withheld (foreign row on a live board) → row is TEASER (locked until
 *     rollover). The client NEVER decides this — it reads the server shape.
 */
export interface LeaderboardRow {
  readonly rank: number;
  /** Owner UUID (server-returned; used to derive the label + own-detection). */
  readonly playerId: string;
  /** Server-formed name part — nick or «Игрок» (rendered at full size). */
  readonly playerName: string;
  /** Server-formed `#`-tag part — non-PII suffix (rendered smaller + muted, §5.3). */
  readonly playerTag: string;
  /**
   * Player progression level — server-truth (`meta_balances.xp` → `levelForXp`, same
   * curve as `/meta/state`). Display-only level badge next to EVERY player's name
   * (owner-decision 2026-06-11), NOT a ranking key (AC-9). Defensively defaults to
   * the curve floor (1) if a row omits it (older server / shape-guard).
   */
  readonly level: number;
  readonly scoreVerified: number;
  readonly linesVerified: number;
  readonly movesCount: number;
  /** ISO-8601 UTC finish timestamp. */
  readonly finishedAt: string;
  /** Replay short-ID, or null when withheld (server §6.3). Drives playable/teaser. */
  readonly shortId: string | null;
  /** True iff this is the viewer's own row (server-computed). */
  readonly isOwn: boolean;
}

/**
 * The viewer's OWN daily row, camelCase-mapped from the server `me` block
 * (§2.3-DONE / §6.7). Independent of pagination — present whenever the
 * authenticated viewer has a daily replay for the queried day, even if their
 * rank is below the returned page. `shortId` is always present (own-replay
 * carve-out) and is what the done-state uses to re-simulate the final board on
 * a fresh device. `null` (absent) for guests / players who didn't play.
 */
export interface MyDailyRow {
  readonly rank: number;
  readonly scoreVerified: number;
  readonly linesVerified: number;
  readonly movesCount: number;
  readonly shortId: string;
  readonly finishedAt: string;
}

/** Successful `GET /daily/leaderboard` response (camelCase-mapped). */
export interface LeaderboardSuccess {
  readonly ok: true;
  /** The board day actually queried (YYYY-MM-DD). */
  readonly dateOfDay: string;
  /** True iff `dateOfDay` is the server-authoritative today (live board). */
  readonly isLive: boolean;
  readonly rows: ReadonlyArray<LeaderboardRow>;
  /** Viewer's own row (rank + own short_id), independent of pagination, or null. */
  readonly me: MyDailyRow | null;
  /** Effective page size after server defaulting + clamping. */
  readonly limit: number;
  /** Effective page offset. */
  readonly offset: number;
}

export type LeaderboardError =
  /** Network / fetch reject — retry-able. */
  | { readonly ok: false; readonly error: 'network' }
  /** 5xx or unexpected response shape — retry-able. */
  | { readonly ok: false; readonly error: 'server_error'; readonly status: number }
  /** 429 — read rate-limit (per-IP); surface + retry-after. */
  | { readonly ok: false; readonly error: 'rate_limited'; readonly status: number };

export type LeaderboardResult = LeaderboardSuccess | LeaderboardError;

export interface GetDailyLeaderboardArgs {
  /** Board day (YYYY-MM-DD). Omit ⇒ server-authoritative today. */
  readonly date?: string;
  /** Page size. Defaults to the server default; clamped server-side. */
  readonly limit?: number;
  /** Page offset (≥ 0). Defaults to 0. */
  readonly offset?: number;
}

/**
 * Client-side page size used by the leaderboard screen's first fetch. Mirrors the
 * server default (single product-wide source — `@lines98/shared/config/balance`),
 * never a local magic number (CLAUDE.md Hard Rule).
 */
export const LEADERBOARD_PAGE_SIZE = DAILY_LEADERBOARD_DEFAULT_LIMIT;

// =============================================================================
// Internal wire shape (server snake_case) — see daily-leaderboard.ts
// =============================================================================

interface LeaderboardRowWire {
  rank: number;
  player_id: string;
  /** Server-formed name part — nick or «Игрок» (ADR rev3 §3 / §5.1). */
  player_name?: string;
  /** Server-formed `#`-tag part — non-PII suffix (§5.3 / Q-6). */
  player_tag?: string;
  /** Player progression level (display-only badge; absent on older servers). */
  level?: number;
  score_verified: number;
  lines_verified: number;
  moves_count: number;
  finished_at: string;
  short_id: string | null;
  is_own: boolean;
}

interface MyDailyRowWire {
  rank: number;
  score_verified: number;
  lines_verified: number;
  moves_count: number;
  short_id: string;
  finished_at: string;
}

interface LeaderboardWireResponse {
  date_of_day: string;
  is_live: boolean;
  rows: LeaderboardRowWire[];
  me: MyDailyRowWire | null;
  limit: number;
  offset: number;
}

/**
 * Floor a wire `level` to the progression-curve base (level 1 = xp 0) when absent /
 * malformed. Single source so daily / all-time / master-jeweler degrade identically —
 * a missing field never renders a NaN badge. `1` is the curve floor (`levelForXp(0)`),
 * not a magic number; matches the server + `/meta/state` default for a no-meta player.
 */
function rowLevelOrFloor(level: number | undefined): number {
  return typeof level === 'number' && Number.isFinite(level) ? Math.max(1, Math.trunc(level)) : 1;
}

// =============================================================================
// Public API
// =============================================================================

/**
 * Fetch a page of the daily leaderboard for `date` (default: server's today).
 *
 * Bearer is attached automatically by api-client.ts if an identity exists — that
 * reveals the viewer's own live `short_id` (§6.3). Works for guests too.
 *
 * @returns `LeaderboardResult` discriminated union. UI branches on `.ok`.
 */
export async function getDailyLeaderboard(
  args: GetDailyLeaderboardArgs = {},
): Promise<LeaderboardResult> {
  const params = new URLSearchParams();
  if (args.date !== undefined) params.set('date', args.date);
  if (args.limit !== undefined) {
    // Defensive clamp to the server max so a caller bug can't trip the schema
    // 400. The server clamps too (single source) — this just avoids a wasted
    // round-trip on an out-of-range request.
    const clamped = Math.min(Math.max(1, args.limit), DAILY_LEADERBOARD_MAX_LIMIT);
    params.set('limit', String(clamped));
  }
  if (args.offset !== undefined) params.set('offset', String(Math.max(0, args.offset)));

  const qs = params.toString();
  const path = qs ? `/daily/leaderboard?${qs}` : '/daily/leaderboard';

  let response;
  try {
    response = await apiClient.get<LeaderboardWireResponse>(path);
  } catch {
    // ApiClientError (network reject) — caller retries.
    return { ok: false, error: 'network' };
  }

  if (!response.ok) {
    if (response.status === 429) {
      return { ok: false, error: 'rate_limited', status: response.status };
    }
    return { ok: false, error: 'server_error', status: response.status };
  }

  const body = response.body;
  // Defensive shape check — if the server pushes a breaking change, fail loudly
  // via `server_error` rather than render NaN / undefined rows.
  if (
    typeof body !== 'object' ||
    body === null ||
    typeof (body as LeaderboardWireResponse).date_of_day !== 'string' ||
    typeof (body as LeaderboardWireResponse).is_live !== 'boolean' ||
    !Array.isArray((body as LeaderboardWireResponse).rows)
  ) {
    return { ok: false, error: 'server_error', status: response.status };
  }

  const wire = body as LeaderboardWireResponse;
  const rows: LeaderboardRow[] = wire.rows.map((r) => ({
    rank: r.rank,
    playerId: r.player_id,
    // Server-formed name + tag (single source, §5.1/§5.3/Q-6); fallback to the
    // shared non-PII parts ONLY if a row omits them (shape-guard / older server).
    // `player_name` is the NAME part only — the tag rides as `player_tag` separately.
    playerName: r.player_name ?? playerDisplayParts(undefined, r.player_id).name,
    playerTag: r.player_tag ?? playerDisplayParts(undefined, r.player_id).tag,
    // Display-only level (server-truth). Floor to the curve base (level 1 = xp 0)
    // if a row omits it (older server / shape-guard) so the badge never shows NaN.
    level: rowLevelOrFloor(r.level),
    scoreVerified: r.score_verified,
    linesVerified: r.lines_verified,
    movesCount: r.moves_count,
    finishedAt: r.finished_at,
    // short_id carries the server's §6.3 visibility decision verbatim.
    shortId: r.short_id ?? null,
    isOwn: r.is_own === true,
  }));

  return {
    ok: true,
    dateOfDay: wire.date_of_day,
    isLive: wire.is_live,
    rows,
    me: mapMyDailyRow(wire.me),
    limit: typeof wire.limit === 'number' ? wire.limit : LEADERBOARD_PAGE_SIZE,
    offset: typeof wire.offset === 'number' ? wire.offset : 0,
  };
}

/**
 * Map the wire `me` block to the camelCase view-model, or `null` when absent /
 * malformed (guest / no daily replay). Defensive — a missing field degrades to
 * `null` (done-state falls back to its simpler card) rather than rendering NaN.
 */
function mapMyDailyRow(wire: MyDailyRowWire | null | undefined): MyDailyRow | null {
  if (!wire || typeof wire !== 'object') return null;
  if (typeof wire.short_id !== 'string' || typeof wire.rank !== 'number') return null;
  return {
    rank: wire.rank,
    scoreVerified: Number(wire.score_verified ?? 0),
    linesVerified: Number(wire.lines_verified ?? 0),
    movesCount: Number(wire.moves_count ?? 0),
    shortId: wire.short_id,
    finishedAt: String(wire.finished_at ?? ''),
  };
}

// =============================================================================
// All-time leaderboard (extra-modes, ADR-0013 §3.4) — per-mode, every row playable
// =============================================================================

/** Client-side page size for the all-time leaderboard's first fetch. */
export const ALL_TIME_LEADERBOARD_PAGE_SIZE = ALL_TIME_LEADERBOARD_DEFAULT_LIMIT;

interface AllTimeRowWire {
  rank: number;
  player_id: string;
  /** Server-formed name part — nick or «Игрок» (ADR rev3 §3 / §5.1). */
  player_name?: string;
  /** Server-formed `#`-tag part — non-PII suffix (§5.3 / Q-6). */
  player_tag?: string;
  /** Player progression level (display-only badge; absent on older servers). */
  level?: number;
  score_verified: number;
  moves_count: number;
  achieved_at: string;
  short_id: string;
  is_own: boolean;
}

interface AllTimeWireResponse {
  mode: GameMode;
  rows: AllTimeRowWire[];
  limit: number;
  offset: number;
}

export interface GetAllTimeLeaderboardArgs {
  readonly mode: GameMode;
  readonly limit?: number;
  readonly offset?: number;
}

/**
 * Fetch a page of the all-time leaderboard for `mode` (extra-modes §2.4 / §5.4).
 * Reuses the same `LeaderboardRow` view-model + the SHARED `playerDisplayParts`
 * (no drift with daily / OG). EVERY row carries its `short_id` (non-daily → the
 * anti-shadow gate does NOT apply, AC-9/AC-19) — so every row is PLAYABLE. There is
 * no live/teaser split here (unlike daily). Returns the same `LeaderboardResult`
 * discriminated union; `dateOfDay` is empty and `isLive` is false (not meaningful
 * for all-time — the screen ignores them).
 */
export async function getAllTimeLeaderboard(
  args: GetAllTimeLeaderboardArgs,
): Promise<LeaderboardResult> {
  const params = new URLSearchParams();
  params.set('mode', args.mode);
  if (args.limit !== undefined) {
    const clamped = Math.min(Math.max(1, args.limit), ALL_TIME_LEADERBOARD_MAX_LIMIT);
    params.set('limit', String(clamped));
  }
  if (args.offset !== undefined) params.set('offset', String(Math.max(0, args.offset)));

  let response;
  try {
    response = await apiClient.get<AllTimeWireResponse>(`/alltime/leaderboard?${params.toString()}`);
  } catch {
    return { ok: false, error: 'network' };
  }

  if (!response.ok) {
    if (response.status === 429) return { ok: false, error: 'rate_limited', status: response.status };
    return { ok: false, error: 'server_error', status: response.status };
  }

  const body = response.body;
  if (typeof body !== 'object' || body === null || !Array.isArray((body as AllTimeWireResponse).rows)) {
    return { ok: false, error: 'server_error', status: response.status };
  }

  const wire = body as AllTimeWireResponse;
  const rows: LeaderboardRow[] = wire.rows.map((r) => ({
    rank: r.rank,
    playerId: r.player_id,
    playerName: r.player_name ?? playerDisplayParts(undefined, r.player_id).name,
    playerTag: r.player_tag ?? playerDisplayParts(undefined, r.player_id).tag,
    level: rowLevelOrFloor(r.level),
    scoreVerified: r.score_verified,
    linesVerified: 0, // not returned by the all-time endpoint (not displayed)
    movesCount: r.moves_count,
    finishedAt: r.achieved_at,
    // Every all-time row is playable (AC-9) — short_id always present.
    shortId: r.short_id,
    isOwn: r.is_own === true,
  }));

  return {
    ok: true,
    dateOfDay: '',
    isLive: false,
    rows,
    // All-time has no per-day «me» row (daily-only concept, §2.3-DONE).
    me: null,
    limit: typeof wire.limit === 'number' ? wire.limit : ALL_TIME_LEADERBOARD_PAGE_SIZE,
    offset: typeof wire.offset === 'number' ? wire.offset : 0,
  };
}
